<template>
  <div class="product-search-picker">
    <div class="product-search-field">
      <i class="fa fa-search text-muted" aria-hidden="true"></i>
      <PrimevueInputText
        v-model="search"
        class="w-100 product-search-input"
        :disabled="disabled"
        :placeholder="placeholder"
        @input="onSearchInput"
        @keydown.enter.prevent="handleEnter"
      />
      <button
        v-if="search"
        type="button"
        class="btn btn-outline-secondary btn-sm btn-action-icon"
        title="Vider"
        :disabled="disabled"
        @click="clearSearch"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div v-if="showStockFilters" class="product-stock-filters mt-2">
      <div class="product-stock-filter-field">
        <label class="form-label small text-muted mb-1" for="product-stock-min">Qté min</label>
        <input
          id="product-stock-min"
          v-model="stockQuantityMin"
          type="number"
          min="0"
          class="form-control form-control-sm"
          :disabled="disabled"
          @input="onSearchInput"
        />
      </div>

      <div class="product-stock-filter-field">
        <label class="form-label small text-muted mb-1" for="product-stock-max">Qté max</label>
        <input
          id="product-stock-max"
          v-model="stockQuantityMax"
          type="number"
          min="0"
          class="form-control form-control-sm"
          :disabled="disabled"
          @input="onSearchInput"
        />
      </div>

      <button
        v-if="hasStockFilters"
        type="button"
        class="btn btn-outline-secondary btn-sm btn-action-icon align-self-end"
        title="Vider les filtres de stock"
        aria-label="Vider les filtres de stock"
        :disabled="disabled"
        @click="clearStockFilters"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger py-2 mt-2 mb-0">
      {{ errorMessage }}
    </div>

    <div v-if="waitingForSearchTerm" class="product-search-results mt-2">
      <div class="product-search-state text-muted">
        Saisissez un mot-clé pour rechercher un produit.
      </div>
    </div>

    <div v-else class="product-search-results mt-2">
      <div v-if="isLoading" class="product-search-state">
        <i class="fa fa-spinner fa-spin me-1"></i>
        Chargement...
      </div>

      <div v-else-if="!products.length" class="product-search-state text-muted">
        Aucun produit.
      </div>

      <template v-else>
        <div
          v-for="product in products"
          :key="product.id"
          class="product-search-row"
          :class="{ 'has-stock': showStock, 'has-price': showPrice }"
        >
          <div class="product-search-info">
            <div class="fw-semibold">{{ product.name }}</div>
            <small class="text-muted">
              {{ product.sku || 'Sans référence' }}
              <span v-if="product.barcode"> · {{ product.barcode }}</span>
            </small>
          </div>

          <div v-if="showStock" class="product-search-meta text-end">
            <span class="small text-muted d-block">Stock</span>
            <span class="badge rounded-pill product-stock-badge" :class="stockBadgeClass(product)">
              {{ stockQuantity(product) }}
            </span>
          </div>

          <div v-if="showPrice" class="product-search-meta text-end">
            <span class="small text-muted d-block">
              {{ product.has_supplier_price ? 'Prix fournisseur' : 'Prix achat' }}
            </span>
            <span class="fw-semibold">{{ $format.currency(purchasePriceFor(product)) }}</span>
          </div>

          <div v-if="isAlreadyAdded(product)" class="product-search-added" role="status">
            <span class="badge text-bg-success">Déjà ajouté</span>
            <small class="text-muted d-block">Modifiez la quantité dans le tableau.</small>
          </div>
          <div v-else-if="showQuantity" class="product-search-quantity">
            <PrimevueInputNumber
              v-model="quickQty[product.id]"
              class="w-100"
              :min="1"
              :maxFractionDigits="0"
              :disabled="disabled"
            />
          </div>

          <button
            v-if="!isAlreadyAdded(product)"
            type="button"
            class="btn btn-outline-primary btn-sm btn-action-icon"
            title="Ajouter"
            :disabled="disabled"
            @click="addProduct(product)"
          >
            <i class="fa fa-plus"></i>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductSearchPicker',
  props: {
    selectedProductIds: {
      type: Array,
      default: null,
    },
    supplierId: {
      type: [Number, String],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Nom, référence ou code-barres...',
    },
    showPrice: {
      type: Boolean,
      default: false,
    },
    showStock: {
      type: Boolean,
      default: true,
    },
    showStockFilters: {
      type: Boolean,
      default: false,
    },
    showQuantity: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    requireSearchTerm: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add'],
  data() {
    return {
      search: '',
      products: [],
      quickQty: {},
      isLoading: false,
      errorMessage: '',
      requestId: 0,
      searchTimer: null,
      stockQuantityMin: '',
      stockQuantityMax: '',
      pendingAddedIds: [],
    };
  },
  mounted() {
    this.fetchProducts();
  },
  beforeUnmount() {
    this.clearSearchTimer();
  },
  watch: {
    supplierId() {
      this.fetchProducts();
    },
    disabled(value) {
      if (!value) {
        this.fetchProducts();
      }
    },
  },
  computed: {
    hasStockFilters() {
      return this.stockQuantityMin !== '' || this.stockQuantityMax !== '';
    },
    normalizedSearchTerm() {
      return String(this.search || '').trim();
    },
    waitingForSearchTerm() {
      return this.requireSearchTerm && this.normalizedSearchTerm === '';
    },
  },
  methods: {
    isAlreadyAdded(product) {
      if (!Array.isArray(this.selectedProductIds)) return false;
      return [...this.selectedProductIds, ...this.pendingAddedIds].some((id) => Number(id) === Number(product.id));
    },
    clearSearchTimer() {
      if (this.searchTimer) {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    onSearchInput() {
      this.clearSearchTimer();
      this.searchTimer = window.setTimeout(() => {
        this.fetchProducts();
      }, 250);
    },
    async fetchProducts() {
      if (this.disabled) return;

      const currentRequestId = this.requestId + 1;
      this.requestId = currentRequestId;

      if (this.waitingForSearchTerm) {
        this.products = [];
        this.errorMessage = '';
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get('/products', {
          params: {
            search: this.search || undefined,
            is_active: true,
            supplier_id: this.supplierId || undefined,
            stock_quantity_min: this.normalizedStockFilter(this.stockQuantityMin),
            stock_quantity_max: this.normalizedStockFilter(this.stockQuantityMax),
            paginate: 1,
            per_page: 10,
          },
        });

        if (currentRequestId !== this.requestId) return;

        this.products = this.extractProducts(response);
        this.products.forEach((product) => {
          if (!Number(this.quickQty[product.id])) {
            this.quickQty[product.id] = 1;
          }
        });
      } catch (error) {
        if (currentRequestId !== this.requestId) return;

        console.error('Erreur recherche produits', error);
        this.products = [];
        this.errorMessage = 'Impossible de charger les produits.';
      } finally {
        if (currentRequestId === this.requestId) {
          this.isLoading = false;
        }
      }
    },
    extractProducts(response) {
      const payload = response?.data?.data ?? response?.data ?? {};

      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;

      return [];
    },
    async handleEnter() {
      if (this.disabled) return;
      if (this.waitingForSearchTerm) return;

      this.clearSearchTimer();

      let exactProduct = this.findExactMatch();

      if (!exactProduct) {
        await this.fetchProducts();
        exactProduct = this.findExactMatch();
      }

      if (exactProduct) {
        this.addProduct(exactProduct, true);
      }
    },
    findExactMatch() {
      const term = String(this.search || '').trim().toLowerCase();
      if (!term) return null;

      return this.products.find((product) => {
        return [product.barcode, product.sku]
          .filter(Boolean)
          .map((value) => String(value).trim().toLowerCase())
          .includes(term);
      }) || null;
    },
    clearSearch() {
      this.search = '';
      this.fetchProducts();
    },
    clearStockFilters() {
      this.stockQuantityMin = '';
      this.stockQuantityMax = '';
      this.fetchProducts();
    },
    normalizedStockFilter(value) {
      if (value === '' || value === null || typeof value === 'undefined') return undefined;

      const number = Number(value);

      return Number.isFinite(number) && number >= 0 ? number : value;
    },
    stockQuantity(product) {
      return Number(product.stock_quantity ?? 0);
    },
    stockThreshold(product) {
      return Number(product.min_stock_quantity ?? 0);
    },
    stockBadgeClass(product) {
      const quantity = this.stockQuantity(product);
      const threshold = this.stockThreshold(product);

      if (quantity <= 1) return 'text-bg-danger';
      if (threshold > 0 && quantity < threshold) return 'text-bg-warning text-dark';

      return 'text-bg-success';
    },
    purchasePriceFor(product) {
      if (product.supplier_purchase_price !== null && typeof product.supplier_purchase_price !== 'undefined') {
        return Number(product.supplier_purchase_price || 0);
      }

      if (product.effective_purchase_price !== null && typeof product.effective_purchase_price !== 'undefined') {
        return Number(product.effective_purchase_price || 0);
      }

      return product.purchase_price != null ? Number(product.purchase_price) : 0;
    },
    quantityFor(product) {
      const quantity = Number(this.quickQty[product.id] || 0);

      return quantity > 0 ? quantity : 1;
    },
    addProduct(product, resetSearch = false) {
      if (this.disabled || !product || this.isAlreadyAdded(product)) return;

      const quantity = this.quantityFor(product);
      // Couvre le double clic avant que les nouvelles props du parent soient rendues.
      if (Array.isArray(this.selectedProductIds)) {
        this.pendingAddedIds.push(product.id);
        this.$nextTick(() => { this.pendingAddedIds = this.pendingAddedIds.filter((id) => id !== product.id); });
      }
      this.$emit('add', { product, quantity });
      this.quickQty[product.id] = 1;

      if (resetSearch) {
        this.search = '';
        this.fetchProducts();
      }
    },
  },
};
</script>

<style scoped>
.product-search-added {
  grid-column: span 2;
  text-align: right;
}
.product-search-field {
  align-items: center;
  border: 1px solid #d9e1ec;
  border-radius: 0.6rem;
  display: flex;
  gap: 0.6rem;
  padding: 0.45rem 0.55rem;
}

.product-search-field :deep(.p-inputtext) {
  border: 0;
  box-shadow: none;
  min-height: 34px;
  padding-left: 0;
}

.product-search-field :deep(.p-inputtext:enabled:focus) {
  box-shadow: none;
}

.product-search-results {
  border: 1px solid #e4ebf5;
  border-radius: 0.65rem;
  overflow: hidden;
}

.product-stock-filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.product-stock-filter-field {
  flex: 1 1 120px;
  min-width: 112px;
}

.product-search-row {
  align-items: center;
  background: #ffffff;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) auto auto;
  min-height: 64px;
  padding: 0.7rem 0.8rem;
}

.product-search-row.has-stock,
.product-search-row.has-price {
  grid-template-columns: minmax(0, 1fr) auto auto auto;
}

.product-search-row.has-stock.has-price {
  grid-template-columns: minmax(0, 1fr) auto auto auto auto;
}

.product-search-state {
  background: #ffffff;
  min-height: 64px;
  padding: 0.7rem 0.8rem;
}

.product-search-row + .product-search-row {
  border-top: 1px solid #edf1f7;
}

.product-search-info {
  min-width: 0;
}

.product-search-info .fw-semibold,
.product-search-info small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-search-meta {
  min-width: 112px;
}

.product-stock-badge {
  min-width: 44px;
}

.product-search-quantity {
  width: 92px;
}

.product-search-quantity :deep(.p-inputnumber-input) {
  text-align: right;
}

@media (max-width: 640px) {
  .product-search-row,
  .product-search-row.has-stock,
  .product-search-row.has-price,
  .product-search-row.has-stock.has-price,
  .product-search-state {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-search-info,
  .product-search-added {
    grid-column: 1 / -1;
    text-align: left;
  }

  .product-search-row > button {
    grid-column: 2;
    justify-self: end;
  }

  .product-search-meta,
  .product-search-quantity {
    min-width: 0;
    width: 100%;
  }
}
</style>
